// Reactive-form validators for the reservation form: the customer-picker checks (booker &
// co-travelers) and the inline accommodation- and fishing-spot-row checks.
import { AbstractControl, FormArray, ValidationErrors, ValidatorFn } from '@angular/forms';
import { PickedCustomer } from '@utils/customer-autocomplete';
import { DateRangeRow } from './reservation-form.model';

// Valid only once a customer has been picked from the list (a resolved object with an id) —
// a raw typed string, or anything without a numeric id, fails. Mirrors what save() consumes.
export function requireSelection(control: AbstractControl): ValidationErrors | null {
  const value = control.value as PickedCustomer | string | null;
  return typeof value === 'object' && value !== null && typeof value.id === 'number'
    ? null
    : { requireSelection: true };
}

// Flags a FormArray of rows (each with a `customer` control) when the same customer is picked
// on more than one row.
export function noDuplicateCustomers(control: AbstractControl): ValidationErrors | null {
  const array = control as FormArray;
  const ids = array.controls
    .map((group) => group.get('customer')?.value)
    .filter((value): value is PickedCustomer => typeof value === 'object' && value !== null)
    .map((value) => value.id);
  return new Set(ids).size !== ids.length ? { duplicateCoTraveler: true } : null;
}

// Row-level: arrival must be strictly before departure. Only flags once both dates are set.
// Shared by accommodation and fishing-spot rows.
export function dateRange(control: AbstractControl): ValidationErrors | null {
  const group = control as DateRangeRow;
  const arrival = group.controls.arrivalDate.value;
  const departure = group.controls.departureDate.value;
  return arrival && departure && arrival.getTime() >= departure.getTime() ? { invalidDateRange: true } : null;
}

// Array-level: the same target (accommodation or fishing spot) cannot have overlapping date
// ranges. Ranges are half-open [arrival, departure): a stay departing the same day another
// arrives is a same-day turnover, not an overlap. This subsumes exact duplicates.
function overlappingRanges(idControl: string, errorKey: string) {
  return (control: AbstractControl): ValidationErrors | null => {
    const array = control as FormArray;
    const stays = array.controls
      .map((g) => {
        const id = g.get(idControl)?.value;
        const from = g.get('arrivalDate')?.value as Date | null;
        const to = g.get('departureDate')?.value as Date | null;
        return id != null && from && to ? { id, from: from.getTime(), to: to.getTime() } : null;
      })
      .filter((s): s is { id: number; from: number; to: number } => s !== null);

    for (let i = 0; i < stays.length; i++) {
      for (let j = i + 1; j < stays.length; j++) {
        const a = stays[i];
        const b = stays[j];
        if (a.id === b.id && a.from < b.to && b.from < a.to) {
          return { [errorKey]: true };
        }
      }
    }
    return null;
  };
}

export const noDuplicateAccommodations = overlappingRanges('accommodationId', 'overlappingAccommodation');
export const noDuplicateFishingSpots = overlappingRanges('fishingSpotId', 'overlappingFishingSpot');

// Row-level: requires a fishing spot ("swim") only while it is selectable — i.e. the water has
// selectable (active, non-dummy) swims and the booking is not whole-water. A null value is valid on
// a whole-water / no-selectable-swims water (the legacy dummy spot). Mirrors validateFishingSpots.
// Selectability is signal-driven in the component, so it is passed in as a predicate rather than
// read here; the returned validator re-evaluates it on every updateValueAndValidity.
export function swimRequiredWhenSelectable(isSelectable: () => boolean): ValidatorFn {
  return (control) => {
    if (control.value != null) return null;
    // Per row since CS-228: a swim may be left unset when THIS row rents the whole lake, so the row's
    // own toggle excuses it — not some other row's. `hasSelectableSwims` still gates the whole check,
    // because a water with nothing to pick can never require a pick.
    const wholeWater = control.parent?.get('wholeWater')?.value === true;
    return isSelectable() && !wholeWater ? { required: true } : null;
  };
}
